import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Clock, Copy, ExternalLink, Eye, Images, LogIn, Pencil, Share2, TimerOff, Trash2 } from 'lucide-react';
import { api } from '../services/api';
import ImagingHeader from './ImagingHeader';
import ImagingEmptyState from './ImagingEmptyState';
import ImagingLoadingState from './ImagingLoadingState';
import ShareEditorModal from './ShareEditorModal';
import Modal from './Modal';
import ShowMoreButton from './ShowMoreButton';
import { dicomHelpers } from '../services/dicomHelpers';
import {
  buildShareLink,
  copyToClipboard,
  describeShareExpiry,
  formatViewCount,
} from '../services/imagingShares';
import './SharesPage.css';

function formatSharedDate(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const COLLAPSED_STUDY_COUNT = 3;

function ShareCard({ share, onEdit, onExpireNow, onDelete, busy }) {
  const [copied, setCopied] = useState(false);
  const [studiesExpanded, setStudiesExpanded] = useState(false);
  const link = buildShareLink(share.token);
  const expiry = describeShareExpiry(share.expiresAt);
  const isExpired = expiry.status === 'expired';
  const studies = share.studies || [];
  const hiddenStudyCount = Math.max(0, studies.length - COLLAPSED_STUDY_COUNT);
  const visibleStudies = studiesExpanded || hiddenStudyCount === 0
    ? studies
    : studies.slice(0, COLLAPSED_STUDY_COUNT);

  useEffect(() => {
    if (!copied) return undefined;
    const timeout = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [copied]);

  const handleCopy = async () => {
    const ok = await copyToClipboard(link);
    setCopied(ok);
  };

  return (
    <div className={`review-card share-card${isExpired ? ' is-expired' : ''}`}>
      <div className="card-line-header share-card-header">
        <div className="share-card-title">
          <Share2 size={16} strokeWidth={2} />
          <span>Shared {formatSharedDate(share.createdAt)}</span>
        </div>
        <button
          type="button"
          className={`share-card-copy-button${copied ? ' is-copied' : ''}`}
          onClick={handleCopy}
          disabled={isExpired}
          title={isExpired ? 'This link has expired' : 'Copy link'}
        >
          {copied ? <Check size={16} strokeWidth={2.5} /> : <Copy size={16} strokeWidth={2} />}
          <span>{copied ? 'Copied' : 'Copy link'}</span>
        </button>
      </div>

      <div className="share-card-body">
        <div className="share-card-stats">
          <span className="share-card-stat" title="Number of times this link has been viewed">
            <Eye size={16} strokeWidth={2} />
            <span>{formatViewCount(share.viewCount)}</span>
          </span>
          <span className={`share-card-stat${isExpired ? ' is-expired' : ''}`}>
            <Clock size={16} strokeWidth={2} />
            <span>{expiry.label}</span>
          </span>
        </div>

        <div className="share-card-studies">
          <div className="share-card-studies-label">
            {studies.length} {studies.length === 1 ? 'study' : 'studies'}
          </div>
          <ul className="share-card-study-list">
            {visibleStudies.map((study) => (
              <li key={study.studyInstanceUID} className="share-card-study">
                <span className="share-card-study-title">{study.description || 'Untitled study'}</span>
                <span className="share-card-study-date">{dicomHelpers.displayDate(study.studyDate)}</span>
              </li>
            ))}
          </ul>
          {hiddenStudyCount > 0 && (
            <ShowMoreButton
              expanded={studiesExpanded}
              hiddenCount={hiddenStudyCount}
              noun={hiddenStudyCount === 1 ? 'study' : 'studies'}
              onToggle={() => setStudiesExpanded((open) => !open)}
            />
          )}
        </div>
      </div>

      <div className="card-footer">
        <div className="card-footer-row share-card-footer-row">
          {!isExpired && (
            <a
              className="comment-button hide-link-styles"
              style={{ color: '#2980b9' }}
              href={link}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={16} strokeWidth={2} /> &nbsp;Open
            </a>
          )}
          <div
            className="comment-button"
            style={{ color: '#2980b9', opacity: busy ? 0.5 : 1, pointerEvents: busy ? 'none' : 'auto' }}
            onClick={() => onEdit(share)}
          >
            <Pencil size={16} strokeWidth={2} /> &nbsp;Edit
          </div>
          {!isExpired && (
            <div
              className="comment-button"
              style={{ color: '#b45309', opacity: busy ? 0.5 : 1, pointerEvents: busy ? 'none' : 'auto' }}
              onClick={() => onExpireNow(share)}
            >
              <TimerOff size={16} strokeWidth={2} /> &nbsp;Expire now
            </div>
          )}
          <div
            className="comment-button"
            style={{ color: '#9e1b1b', opacity: busy ? 0.5 : 1, pointerEvents: busy ? 'none' : 'auto' }}
            onClick={() => onDelete(share)}
          >
            <Trash2 size={16} strokeWidth={2} /> &nbsp;Delete
          </div>
        </div>
      </div>
    </div>
  );
}

function SharesPage({ toast, user, onLogin }) {
  const navigate = useNavigate();
  const [shares, setShares] = useState([]);
  const [studies, setStudies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingShare, setEditingShare] = useState(null);
  const [deletingShare, setDeletingShare] = useState(null);
  const [busyShareId, setBusyShareId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return undefined;
    }
    let cancelled = false;
    setIsLoading(true);
    Promise.all([api.getImagingShares(), api.getImagingFolders()])
      .then(([sharesResponse, foldersResponse]) => {
        if (cancelled) return;
        setShares(sharesResponse || []);
        setStudies(foldersResponse || []);
      })
      .catch((err) => {
        console.log('SharesPage load error:', err);
        if (!cancelled) toast('Failed to load your shares.', 'warning');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [user]);

  const replaceShare = (updated) => {
    setShares((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
  };

  const handleEditSubmit = async ({ studyInstanceUIDs, expiresAt }) => {
    const updated = await api.updateImagingShare(editingShare.id, { studyInstanceUIDs, expiresAt });
    replaceShare(updated);
    toast('Share updated.');
    return updated;
  };

  const handleExpireNow = async (share) => {
    setBusyShareId(share.id);
    try {
      const updated = await api.updateImagingShare(share.id, {
        studyInstanceUIDs: (share.studies || []).map((s) => s.studyInstanceUID),
        expiresAt: new Date().toISOString(),
      });
      replaceShare(updated);
      toast('Share link expired.');
    } catch (err) {
      console.log('handleExpireNow error:', err);
      toast('Failed to expire share.', 'warning');
    } finally {
      setBusyShareId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingShare) return;
    setDeleting(true);
    try {
      await api.deleteImagingShare(deletingShare.id);
      setShares((prev) => prev.filter((s) => s.id !== deletingShare.id));
      toast('Share deleted.');
      setDeletingShare(null);
    } catch (err) {
      console.log('handleConfirmDelete error:', err);
      toast('Failed to delete share.', 'warning');
    } finally {
      setDeleting(false);
    }
  };

  const renderBody = () => {
    if (!user) {
      return (
        <ImagingEmptyState
          title="Log in to see your shares"
          description="Your shareable links and their view counts will appear here once you log in."
          actionLabel="Log In"
          onAction={onLogin}
          Icon={Share2}
          ActionIcon={LogIn}
        />
      );
    }
    if (isLoading) {
      return (
        <ImagingLoadingState
          title="Loading shares"
          description="Hang tight while we fetch your shareable links."
        />
      );
    }
    if (shares.length === 0) {
      return (
        <ImagingEmptyState
          title="No shares yet"
          description="Select one or more studies on Your Images and create a shareable link. Shares you create will show up here along with their view counts."
          actionLabel="Go to Your Images"
          onAction={() => navigate('/images')}
          Icon={Share2}
          ActionIcon={Images}
        />
      );
    }
    return (
      <div className="reviews">
        {shares.map((share) => (
          <ShareCard
            key={share.id}
            share={share}
            busy={busyShareId === share.id}
            onEdit={setEditingShare}
            onExpireNow={handleExpireNow}
            onDelete={setDeletingShare}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="review-list-container" style={{ paddingTop: '0px' }}>
      <ImagingHeader
        activeTab="shares"
        counts={user && !isLoading ? { images: studies.length, shares: shares.length } : undefined}
      />

      {renderBody()}

      <ShareEditorModal
        isOpen={!!editingShare}
        onClose={() => setEditingShare(null)}
        mode="edit"
        studies={studies}
        initialSelectedUIDs={(editingShare?.studies || []).map((s) => s.studyInstanceUID)}
        initialExpiresAt={editingShare?.expiresAt || null}
        onSubmit={handleEditSubmit}
      />

      <Modal
        isOpen={!!deletingShare}
        onClose={() => (deleting ? null : setDeletingShare(null))}
        title="Delete Share"
      >
        <div className="helper-text">
          <div>Are you sure you want to delete this share?</div>
          <div>Anyone with the link will no longer be able to view these studies.</div>
        </div>
        <div className="share-delete-actions">
          <button
            type="button"
            className="imaging-action-button outline"
            onClick={() => setDeletingShare(null)}
            disabled={deleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="imaging-action-button share-delete-confirm"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            <Trash2 size={18} strokeWidth={2} />
            <span>{deleting ? 'Deleting…' : 'Delete'}</span>
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default SharesPage;
